import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import Page from "components/page";
import { Paths } from "paths";

const Deploy = () => {
  const [t] = useTranslation('common');

  return (
    <Page title={t('Deploy LocalIQ LLM Inference Server')} description={t('Step-by-step guide for deploying your own on-premise LLM inference server with LocalIQ. Set up a secure, scalable AI infrastructure.')} canonicalUrl={Paths.Deploy.path}>
        <Container className="min-vh-100 my-5">
            <Row className="justify-content-md-center">
                <Col md={8} lg={8}>
                    <article className="deployment-guide">
                        <h1 className="mb-4 text-center">{t('Deploy LocalIQ LLM Inference Server')}</h1>
                        
                        <div className="article-content">                            
                            <h3 className="mt-4 mb-3">Prerequisites</h3>
                            <ul className="list-unstyled ms-3">
                                <li>• Linux server with x86-64 architecture for the LocalIQ server</li>
                                <li>• Linux server with GPUs for running the LLM worker</li>
                                <li>• Network connection between both servers</li>
                                <li>• Docker installed on the LocalIQ server</li>
                                <li>• Docker Compose installed on the LocalIQ server</li>
                            </ul>
                            
                            <h3 className="mt-4 mb-3">Deployment Steps</h3>
                            
                            <h4 className="mt-4 mb-3">1. Pull the Docker Image</h4>
                            <pre className="code-block bg-light p-3 rounded"><code>docker pull cydanix/localiqai-server:latest</code></pre>

                            <h4 className="mt-4 mb-3">2. Download the LocalIQ Server Docker Compose File</h4>
                            <p>Download the <code>docker-compose.yml</code> file from <a href={window.location.origin + "/deploy/onpremise/docker-compose.yml"}>this link</a>.</p>

                            <h4 className="mt-4 mb-3">3. Download the LocalIQ Server Environment Generation Script</h4>
                            <p>Download the <code>gen_env.sh</code> script from <a href={window.location.origin + "/deploy/onpremise/gen_env.sh"}>this link</a>.</p>
                            <p>Execute the following command to generate the environment file <code>.env</code>:</p>
                            <pre className="code-block bg-light p-3 rounded"><code>chmod +x ./gen_env.sh && ./gen_env.sh</code></pre>

                            <h4 className="mt-4 mb-3">4. Configure Environment Variables</h4>
                            <p>Edit the <code>.env</code> file with your specific configuration settings.</p>
                            <p>Set <code>TLS_INSECURE</code> to <code>true</code> in the <code>.env</code> file to use an insecure connection to the server.</p>

                            <h4 className="mt-4 mb-3">5. Generate Server TLS Certificates and Keys (Optional)</h4>
                            <div className="collapsible-section">
                                <details>
                                    <summary className="clickable-summary">TLS Certificate Generation (click to expand)</summary>
                                    <div className="collapsible-content">
                                        <p>Create a folder to store the TLS certificates and keys.</p>
                                        <p>Navigate to this folder and download the <code>gen_tls_keys.sh</code> script from <a href={window.location.origin + "/deploy/onpremise/gen_tls_keys.sh"}>this link</a>.</p>
                                        <p>Execute the following command to generate the TLS certificates and keys:</p>
                                        <pre className="code-block bg-light p-3 rounded"><code>./gen_tls_keys.sh TLS_SERVER_NAME TLS_SERVER_ALT_IP</code></pre>
                                        <p>Replace <code>TLS_SERVER_NAME</code> with your server's domain name or IP address, and <code>TLS_SERVER_ALT_IP</code> with an optional alternative IP address for the server.</p>
                                        <p>In your <code>.env</code> file, set <code>TLS_KEYS_DIR</code> to the absolute path of the folder containing your TLS certificates and keys.</p>
                                        <p>In your <code>.env</code> file, set <code>TLS_SERVER_NAME</code> to the same value you used for <code>TLS_SERVER_NAME</code> or <code>TLS_SERVER_ALT_IP</code>.</p>
                                    </div>
                                </details>
                            </div>

                            <h4 className="mt-4 mb-3">6. Start the Services</h4>
                            <p>Run the following command in the directory containing your <code>docker-compose.yml</code> file to start all services:</p>
                            <pre className="code-block bg-light p-3 rounded"><code>docker-compose up -d</code></pre>
                            
                            <h4 className="mt-4 mb-3">7. Verify Deployment</h4>
                            <p>Check if all services are running properly with:</p>
                            <pre className="code-block bg-light p-3 rounded"><code>docker-compose ps</code></pre>

                            <p className="mt-4">Once deployed, you can access the web interface at <a href="https://SERVER_DNS_OR_IP:8080">https://SERVER_DNS_OR_IP:8080</a>.</p>
                            <p>Or use <a href="http://SERVER_DNS_OR_IP:8080">http://SERVER_DNS_OR_IP:8080</a> if you set <code>TLS_INSECURE</code> to <code>true</code> in the <code>.env</code> file.</p>
                            <p>After accessing the interface, create an account to begin using the service.</p>

                            <p>To start serving LLM inference requests, navigate to the "Add Worker" page to deploy your first worker. This will enable you to process AI model requests through your newly deployed infrastructure.</p>
                        </div>
                    </article>
                </Col>
            </Row>
        </Container>
    </Page>
  );
};

export default Deploy;